/**
 * CacheSizeManager - Utility for managing cache size limits
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for managing cache size by removing oldest entries
 * when the cache exceeds a specified limit.
 */

/**
 * Remove oldest entry from cache if it exceeds size limit
 * 
 * Checks if the cache size exceeds the limit and removes the oldest entry
 * (first key from iterator) if necessary.
 * 
 * @param cache - Map to manage
 * @param maxSize - Maximum cache size before eviction
 * @returns True if an entry was removed, false otherwise
 */
export function evictOldestIfNeeded<T>(
  cache: Map<string, T>,
  maxSize: number
): boolean {
  if (cache.size > maxSize) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey) {
      cache.delete(oldestKey);
      return true;
    }
  }
  return false;
}

/**
 * Remove oldest entries from cache until size is within limit
 * 
 * Removes entries one by one until the cache size is within the limit.
 * 
 * @param cache - Map to manage
 * @param maxSize - Maximum cache size
 * @returns Number of entries removed
 */
export function evictUntilWithinLimit<T>(
  cache: Map<string, T>,
  maxSize: number
): number {
  let removed = 0;
  while (cache.size > maxSize) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey) {
      cache.delete(oldestKey);
      removed++;
    } else {
      break; // No more keys to remove
    }
  }
  return removed;
}

/**
 * Get cache size status
 * 
 * @param cache - Map to check
 * @param maxSize - Maximum cache size
 * @returns Object with size information
 */
export function getCacheSizeStatus<T>(
  cache: Map<string, T>,
  maxSize: number
): { size: number; maxSize: number; isOverLimit: boolean; utilizationPercent: number } {
  const size = cache.size;
  return {
    size,
    maxSize,
    isOverLimit: size > maxSize,
    utilizationPercent: maxSize > 0 ? (size / maxSize) * 100 : 0
  };
}


